import { TEST_IDS } from '@va/constants';
import { EraseIcon, SearchIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { useOutsideClick } from '@va/util/hooks';
import classNames from 'classnames';
import { ChangeEvent, RefObject, useRef, useState } from 'react';

type SearchFieldProps = {
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  clearField: () => void;
  value: string;
  name?: string;
  id?: string;
  placeholder?: string;
  className?: string;
  'data-testid'?: string;
  size?: 'small' | 'large';
  inputRef?: RefObject<HTMLInputElement | null>;
};

export const SearchField: React.FC<SearchFieldProps> = ({
  value,
  onChange,
  name,
  id,
  placeholder,
  clearField,
  className,
  'data-testid': dataTestId,
  size = 'large',
  inputRef,
}) => {
  const [focused, setFocused] = useState(false);

  const translate = useTranslate();
  const inputContainerRef = useRef<HTMLDivElement>(null);

  useOutsideClick(inputContainerRef, () => {
    setFocused(false);
  });

  return (
    <div
      ref={inputContainerRef}
      onClick={() => {
        setFocused(true);
      }}
      className={classNames('border overflow-hidden flex gap-3 items-center', className, {
        'h-12 p-1 rounded-12': size === 'large',
        'h-9 px-3 rounded-lg': size === 'small',
        'border-primary bg-white': focused,
        'border-transparent bg-white-snow': !focused,
      })}
      data-testid={dataTestId}
    >
      <SearchIcon
        className={classNames('shrink-0', {
          'w-4 h-4': size === 'small',
        })}
        color='#696969'
      />
      <input
        ref={inputRef}
        className={classNames('w-full h-full bg-transparent placeholder-gray-devil outline-hidden!', {
          'p-2': size === 'large',
          'text-xs font-medium': size === 'small',
        })}
        value={value}
        onChange={onChange}
        name={name}
        id={id}
        placeholder={placeholder ?? translate('labels.search')}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />

      {value !== '' && (
        <button
          onClick={clearField}
          className={classNames(
            'aspect-square rounded-md flex items-center shrink-0 justify-center hover:bg-gray-concrete',
            {
              'bg-white': !focused,
              'bg-white-snow': focused,
              'w-4 h-4': size === 'small',
              'h-full': size === 'large',
            },
          )}
          data-testid={TEST_IDS.generic.buttons.erase}
        >
          <EraseIcon className='text-gray-charcoal' />
        </button>
      )}
    </div>
  );
};
